import React, { useCallback, useEffect, useRef, useState } from "react";
import { useBannerStore } from "../../../../Store/Banner";
import { Banner, BannerType } from "../../../../Contracts/Banner";
import { ReactComponent as CloseIcon } from "../../../../Assets/ActionIcons/CloseIcon.svg";
import { ReactComponent as CheckMarkIcon } from "../../../../Assets/ActionIcons/CheckMarkIcon.svg";
import { ReactComponent as ExclamationWarningIcon } from "../../../../Assets/ActionIcons/ExclamationWarningIcon.svg";
import { ReactComponent as InfoIcon } from "../../../../Assets/ActionIcons/InfoIcon.svg";

const MessageBanner: React.FC = () => {
  const { activeBanners, removeBanner } = useBannerStore();
  const [displayedBanners, setDisplayedBanners] = useState<Banner[]>([]);
  const [removingBanners, setRemovingBanners] = useState<string[]>([]);
  const timeoutsRef = useRef<{ [id: string]: NodeJS.Timeout }>({});

  const handleRemoveBanner = useCallback(
    (banner: Banner) => {
      setRemovingBanners(prev => [...prev, banner.id]);
      setTimeout(() => {
        removeBanner(banner);
        setRemovingBanners(prev => prev.filter(id => id !== banner.id));
      }, 1000);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    const bannersToSet = activeBanners.slice(-3);
    setDisplayedBanners(bannersToSet);

    bannersToSet.forEach(banner => {
      if (!banner.closeable && !timeoutsRef.current[banner.id]) {
        const duration =
          banner.duration ||
          Math.min(10000, Math.max(3000, (banner.message || "").split(" ").length * 200));
        timeoutsRef.current[banner.id] = setTimeout(() => {
          handleRemoveBanner(banner);
          delete timeoutsRef.current[banner.id];
        }, duration);
      }
    });

    Object.keys(timeoutsRef.current).forEach(id => {
      if (!bannersToSet.find(b => b.id === id)) {
        clearTimeout(timeoutsRef.current[id]);
        delete timeoutsRef.current[id];
      }
    });
  }, [activeBanners, handleRemoveBanner]);

  return (
    <div className="absolute w-full flex flex-col items-center justify-center mt-12 z-40">
      {displayedBanners.map((banner, index) => {
        let bannerColor = "gray";
        let icon: React.ReactNode | undefined;
        switch (banner.type) {
          case BannerType.Success:
            bannerColor = "lime";
            icon = <CheckMarkIcon className="w-3 h-3 text-lime-500" />;
            break;
          case BannerType.Error:
            bannerColor = "red";
            icon = <ExclamationWarningIcon className="w-2 h-2 text-red-500" />;
            break;
          case BannerType.Warning:
            bannerColor = "yellow";
            icon = <ExclamationWarningIcon className="w-2 h-2 text-yellow-500" />;
            break;
          case BannerType.Info:
            bannerColor = "blue";
            icon = <InfoIcon className="w-2 h-2 text-blue-500" />;
            break;
        }
        const opacity =
          index === displayedBanners.length - 1
            ? 1
            : index === displayedBanners.length - 2
              ? 0.5
              : 0.2;
        const topOffset = 45 - (displayedBanners.length - index) * 15;
        const isRemoving = removingBanners.includes(banner.id);
        return (
          <div
            key={banner.id}
            className={`absolute bg-${bannerColor}-100 text-${bannerColor}-800 font-semibold text-sm rounded-lg px-4 py-3 m-2 flex items-center space-x-3 border-b-2 border-${bannerColor}-500 max-w-2xl`}
            style={{
              opacity: isRemoving ? 0 : opacity,
              transform: `translateY(${topOffset}px) ${isRemoving ? "translateX(70%)" : ""}`,
              transition: "opacity 2s, transform 2s",
              zIndex: index
            }}
          >
            {icon}
            <div>{banner.message}</div>
            {banner.closeable && (
              <button className="cursor-pointer" onClick={() => handleRemoveBanner(banner)}>
                <CloseIcon className="w-2 h-2" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MessageBanner;
